import { usePlatform } from "@portal/hooks/usePlatform";
import { type KeyboardEvent, type RefObject, useRef } from "react";
import { useDataGridNavigation } from "./DataGridNavContext";
import { useDataGridDispatch, useDataGridState } from "./state/DataGridStateProvider";
import type { DataGridCellAddr } from "./types";
import { cellAddrEquals, computeNextCell, type MoveDirection } from "./utils/buildNavigationMatrix";

/** Inset ring using blue[500] — not in the ring safelist so applied via inline style. */
export const ACTIVE_CELL_RING = { boxShadow: "inset 0 0 0 2px #3B82F6" } as const;

export type DataGridCellNavResult = {
	cellRef: RefObject<HTMLDivElement | null>;
	isActive: boolean;
	isFocusable: boolean;
	/** The first scrolling row takes an oversized margin so focusing it scrolls the grid to the top;
	 *  every other cell clears the sticky header block. */
	scrollMarginClass: string;
	moveActive: (direction: MoveDirection) => void;
	handleFocus: () => void;
	handleNavKey: (e: KeyboardEvent) => boolean;
	handleCopyKey: (e: KeyboardEvent<HTMLElement>) => boolean;
};

const ARROW_DIRECTIONS: Record<string, MoveDirection> = {
	ArrowUp: "up",
	ArrowDown: "down",
	ArrowLeft: "left",
	ArrowRight: "right",
};

/** Shared navigation logic for DataGrid cell components.
 *  Owns active-cell tracking, first-cell fallback focus, and arrow/tab movement. */
export const useDataGridCellNav = (rowId: string, colId: string): DataGridCellNavResult => {
	const state = useDataGridState();
	const dispatch = useDataGridDispatch();
	const { matrix, firstScrollingRowId } = useDataGridNavigation();
	const { isMac } = usePlatform();
	const cellRef = useRef<HTMLDivElement>(null);

	const addr: DataGridCellAddr = { rowId, colId };

	const isActive = state.activeCell !== null && cellAddrEquals(state.activeCell, addr);
	const isFirstFallback =
		state.activeCell === null && matrix[0]?.[0] !== undefined && cellAddrEquals(matrix[0][0], addr);
	const isFocusable = isActive || isFirstFallback;
	const scrollMarginClass =
		firstScrollingRowId === rowId
			? "scroll-mt-[9999px]"
			: "scroll-mt-[var(--datagrid-sticky-offset,0px)]";

	const moveActive = (direction: MoveDirection) => {
		const next = computeNextCell(matrix, addr, direction);
		if (next && !cellAddrEquals(next, addr)) {
			dispatch({ type: "set_active_cell", cell: next });
		}
	};

	const handleFocus = () => {
		if (!isActive) dispatch({ type: "set_active_cell", cell: addr });
	};

	const handleNavKey = (e: KeyboardEvent): boolean => {
		const arrowDirection = ARROW_DIRECTIONS[e.key];
		if (arrowDirection) {
			e.preventDefault();
			moveActive(arrowDirection);
			return true;
		}
		if (e.key === "Tab") {
			e.preventDefault();
			moveActive(e.shiftKey ? "prevWrap" : "nextWrap");
			return true;
		}
		return false;
	};

	/** Copies the focused cell's text to the clipboard on the platform copy chord
	 *  (⌘C on macOS, Ctrl+C elsewhere). Returns true when it handled the event. */
	const handleCopyKey = (e: KeyboardEvent<HTMLElement>): boolean => {
		const usesPlatformModifier = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
		if (!usesPlatformModifier || (e.key !== "c" && e.key !== "C")) return false;
		e.preventDefault();
		const text = cellRef.current?.textContent?.trim() ?? "";
		void navigator.clipboard?.writeText(text);
		return true;
	};

	return {
		cellRef,
		isActive,
		isFocusable,
		scrollMarginClass,
		moveActive,
		handleFocus,
		handleNavKey,
		handleCopyKey,
	};
};
